export const STATE_ROUTE_ROOT: string = '/avatar';

export function isInternalStateRouteUrl(url: string): boolean {
  const normalizedUrl: string = url.trim();
  if (!normalizedUrl || normalizedUrl.startsWith('//')) return false;
  if (normalizedUrl.includes('\\') || /[\u0000-\u001F\u007F]/.test(normalizedUrl)) return false;
  return !/^[a-z][a-z\d+.-]*:/i.test(normalizedUrl);
}

export function normalizeStateRouteUrl(url: string): string {
  const trimmedUrl: string = url.trim();
  if (!isInternalStateRouteUrl(trimmedUrl)) return STATE_ROUTE_ROOT;

  const normalizedUrl: string = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
  const queryIndex: number = normalizedUrl.indexOf('?');
  const hashIndex: number = normalizedUrl.indexOf('#');
  const suffixIndex: number = Math.min(
    queryIndex >= 0 ? queryIndex : normalizedUrl.length,
    hashIndex >= 0 ? hashIndex : normalizedUrl.length,
  );
  const path: string = normalizeStateRoutePath(normalizedUrl.slice(0, suffixIndex));
  return `${path}${normalizedUrl.slice(suffixIndex)}`;
}

export function normalizeStateRoutePath(url: string): string {
  const queryIndex: number = url.indexOf('?');
  const hashIndex: number = url.indexOf('#');
  const endIndex: number = Math.min(
    queryIndex >= 0 ? queryIndex : url.length,
    hashIndex >= 0 ? hashIndex : url.length,
  );
  const rawPath: string = url.slice(0, endIndex);
  if (!rawPath || rawPath === '/') return STATE_ROUTE_ROOT;

  const path: string = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

export function resolveStateRouteFallbackUrl(url: string): string | null {
  const normalizedUrl: string = normalizeStateRouteUrl(url);
  const path: string = normalizeStateRoutePath(normalizedUrl);
  const segments: readonly string[] = path.split('/').filter(Boolean);

  if (path.startsWith('/wallet/transfer/')) {
    return resolveWalletTransferFallback(normalizedUrl, segments);
  }
  if (path === '/wallet/transfer') return '/wallet';
  if (path.startsWith('/chats/')) return resolveChatFallback(segments);
  if (path.startsWith('/people/')) return resolvePeopleFallback(segments);
  if (path.startsWith('/@')) return '/people';
  if (path === '/profile/settings/phone-change-confirm') return '/profile/settings/personal-info';
  if (/^\/profile\/settings\/user-skills\/[^/]+$/.test(path)) return '/profile/settings/user-skills';
  if (path.startsWith('/profile/settings/')) return '/profile/settings';
  if (path === '/profile/settings') return '/profile';
  if (path.startsWith('/profile/')) return '/profile';
  if (path.startsWith('/tasks/')) return resolveTaskFallback(segments);
  if (path.startsWith('/reply/')) return resolveFlowFallback(segments);
  if (path.startsWith('/extra-work/')) return resolveFlowFallback(segments);
  return null;
}

function resolveWalletTransferFallback(url: string, segments: readonly string[]): string {
  const queryStart: number = url.indexOf('?');
  const query: string = queryStart >= 0 ? url.slice(queryStart + 1).split('#')[0] : '';
  const chatId: string | null = new URLSearchParams(query).get('chatId');
  if (chatId && /^\d+$/.test(chatId)) return `/chats/${chatId}`;

  const recipientId: string | undefined = segments[2];
  return recipientId ? `/chats/user/${recipientId}` : '/wallet';
}

function resolveChatFallback(segments: readonly string[]): string {
  if (
    segments.length <= 2
    || segments[1] === 'group'
    || segments[1] === 'share'
    || segments[1] === 'user'
  ) {
    return '/chats';
  }
  return `/${segments.slice(0, 2).join('/')}`;
}

function resolvePeopleFallback(segments: readonly string[]): string {
  return segments.length >= 4 && segments[2] === 'file'
    ? `/${segments.slice(0, 2).join('/')}`
    : '/people';
}

function resolveTaskFallback(segments: readonly string[]): string {
  const flowSegment: string = segments[1] ?? '';
  if (['add', 'draft', 'filter', 'choose-category', 'my-tasks'].includes(flowSegment)) {
    return '/tasks';
  }
  if (['edit', 'dispute'].includes(flowSegment)) {
    return segments.length >= 3 ? `/tasks/${segments[2]}` : '/tasks';
  }
  return segments.length > 2 ? `/${segments.slice(0, 2).join('/')}` : '/tasks';
}

function resolveFlowFallback(segments: readonly string[]): string | null {
  return segments.length > 2 ? `/${segments.slice(0, 2).join('/')}` : null;
}
